import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from './GiftingReviewsCorporateBrandRefresh.module.css';
import { GIFTING_REVIEWS_CORPORATE_DEFAULT } from './giftingReviewsCorporateConstants';

const reviewCardTexture = require('./imgs/review-card-texture.png');
const reviewCookieTop = require('./imgs/review-cookie-top.png');
const reviewCookieBottom = require('./imgs/review-cookie-bottom.png');
const verifiedCheckIcon = require('./imgs/verified-check.svg');
const corporatePhoto = require('./imgs/corporate-photo.png');
const orderInBulkBadge = require('./imgs/order-in-bulk-badge.svg');

function GiftingReviewsCorporateBrandRefresh({
  reviews,
  reviewRotationMs,
  corporateEyebrow,
  corporateHeadline,
  corporateBody,
  primaryCtaText,
  primaryCtaLink,
  primaryCtaButtonType,
  secondaryCtaText,
  secondaryCtaLink,
  secondaryCtaButtonType,
  contentSectionCorporate,
  onCtaClick,
  corporatePhotoSrc,
}) {
  const [activeReviewIndex, setActiveReviewIndex] = useState(0);
  const reviewCount = reviews.length;

  const goToReview = useCallback(
    index => {
      if (reviewCount === 0) return;
      setActiveReviewIndex((index + reviewCount) % reviewCount);
    },
    [reviewCount]
  );

  useEffect(() => {
    if (reviewCount <= 1 || !reviewRotationMs) return undefined;

    const prefersReducedMotion =
      typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) return undefined;

    const timer = window.setInterval(() => {
      setActiveReviewIndex(prev => (prev + 1) % reviewCount);
    }, reviewRotationMs);

    return () => window.clearInterval(timer);
  }, [reviewCount, reviewRotationMs]);

  const activeReview = reviews[activeReviewIndex] || reviews[0];

  const handlePrimaryCta = () => {
    if (onCtaClick && primaryCtaLink) {
      onCtaClick(
        primaryCtaLink,
        primaryCtaButtonType,
        primaryCtaText,
        contentSectionCorporate
      );
    }
  };

  const handleSecondaryCta = () => {
    if (onCtaClick && secondaryCtaLink) {
      onCtaClick(
        secondaryCtaLink,
        secondaryCtaButtonType,
        secondaryCtaText,
        contentSectionCorporate
      );
    }
  };

  return (
    <section
      className={styles.section}
      data-region="gifting-reviews-corporate"
      aria-label="Customer reviews and corporate gifting"
    >
      <div className={styles.sectionCanvas}>
        <div className={styles.reviewsBlock} data-part="gifting-reviews">
          <div className={styles.reviewCookieBottomWrap} aria-hidden="true">
            <img src={reviewCookieBottom} alt="" className={styles.reviewCookieBottom} />
          </div>

          <div className={styles.reviewsStage}>
            <div className={styles.reviewCard}>
              <div className={styles.reviewCardBg} aria-hidden="true">
                <div className={styles.reviewCardBgColor} />
                <img src={reviewCardTexture} alt="" className={styles.reviewCardTexture} />
              </div>

              <div
                className={styles.reviewCarousel}
                data-part="review-carousel"
                aria-live="polite"
                aria-atomic="true"
              >
                <div className={styles.reviewContent}>
                  <p className={styles.reviewAuthor}>{activeReview.author}</p>
                  <blockquote className={styles.reviewQuote}>{activeReview.quote}</blockquote>
                  <div className={styles.verifiedBadge}>
                    <img src={verifiedCheckIcon} alt="" className={styles.verifiedIcon} />
                    <span>Verified review</span>
                  </div>
                </div>

                <div
                  className={styles.pagination}
                  role="tablist"
                  aria-label="Review slides"
                >
                  {reviews.map((review, index) => {
                    const isActive = index === activeReviewIndex;
                    return (
                      <button
                        key={review.id}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        aria-label={`Review ${index + 1} of ${reviewCount}`}
                        className={isActive ? styles.paginationDotActive : styles.paginationDot}
                        onClick={() => goToReview(index)}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
            <div className={styles.reviewCookieTopWrap} aria-hidden="true">
              <div className={styles.reviewCookieTopRotate}>
                <img src={reviewCookieTop} alt="" className={styles.reviewCookieTop} />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.corporateBlock} data-part="gifting-corporate">
          <div className={styles.corporateLayout}>
            <div className={styles.corporateCopyColumn}>
              <div className={styles.corporateIntro}>
                <p className={styles.corporateEyebrow}>{corporateEyebrow}</p>
                <h2 className={styles.corporateHeadline}>{corporateHeadline}</h2>
              </div>
              <div className={styles.corporateDetails}>
                <p className={styles.corporateBody}>{corporateBody}</p>
                <div className={styles.corporateCtas}>
                  <button
                    type="button"
                    className={styles.ctaPrimary}
                    data-part="corporate-cta-primary"
                    onClick={handlePrimaryCta}
                  >
                    {primaryCtaText}
                  </button>
                  <button
                    type="button"
                    className={styles.ctaSecondary}
                    data-part="corporate-cta-secondary"
                    onClick={handleSecondaryCta}
                  >
                    {secondaryCtaText}
                  </button>
                </div>
              </div>
            </div>

            <div className={styles.corporateMedia}>
              <img
                src={corporatePhotoSrc || corporatePhoto}
                alt="Hands reaching into a box of Insomnia Cookies"
                className={styles.corporatePhoto}
              />
              <img
                src={orderInBulkBadge}
                alt=""
                className={styles.bulkBadge}
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

GiftingReviewsCorporateBrandRefresh.propTypes = {
  reviews: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      author: PropTypes.string.isRequired,
      quote: PropTypes.string.isRequired,
    })
  ),
  reviewRotationMs: PropTypes.number,
  corporateEyebrow: PropTypes.string,
  corporateHeadline: PropTypes.string,
  corporateBody: PropTypes.string,
  primaryCtaText: PropTypes.string,
  secondaryCtaText: PropTypes.string,
  primaryCtaLink: PropTypes.string,
  secondaryCtaLink: PropTypes.string,
  primaryCtaButtonType: PropTypes.string,
  secondaryCtaButtonType: PropTypes.string,
  contentSectionCorporate: PropTypes.string,
  onCtaClick: PropTypes.func,
  corporatePhotoSrc: PropTypes.string,
};

GiftingReviewsCorporateBrandRefresh.defaultProps = {
  ...GIFTING_REVIEWS_CORPORATE_DEFAULT,
  onCtaClick: null,
  corporatePhotoSrc: null,
};

export default GiftingReviewsCorporateBrandRefresh;
